import { KeyboardEvent, useCallback } from 'react'
import { styled, CircularProgress } from '@mui/material'
import {
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  Title,
} from '..'
import { Button } from '../../../../../components/buttons'
import { intl } from '../../../../../../i18n'
import TextInput from '../../../../../components/editors/input/TextInput'
import { colorPalette } from '../../../../../style/colorPalette'
import { useReportDialog } from './hooks/useReportDialog'
import { VersionReportVersionEntity } from '../../../../../../domain/entity/version-report/VersionReportVersionEntity'

type ReportDialogProps = {
  open: boolean
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  original: VersionReportVersionEntity
}

export const ReportDialog = ({
  open,
  onAfterSubmit,
  onClose,
  original,
}: ReportDialogProps) => {
  const { reportName, setReportName, loading, handleSubmit, handleClose } =
    useReportDialog({ onAfterSubmit, onClose, original })

  const hasError = !reportName.trim()
  const isSubmitDisabled = loading || hasError

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !isSubmitDisabled) {
        handleSubmit()
      }
    },
    [handleSubmit, isSubmitDisabled]
  )

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <StyledDialogTitle>
        <Title>報告書名の編集</Title>
      </StyledDialogTitle>
      <StyledDialogContent>
        <ReportNameInput
          label="報告書名"
          value={reportName}
          onChange={e => setReportName(e.target.value)}
          onKeyDown={handleKeyDown}
          required={true}
          disabled={loading}
          variant="outlined"
          error={hasError}
          helperText={hasError ? '報告書名を入力してください' : ''}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <SubmitButtonContainer>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            colorPattern="skyBlue"
          >
            {loading ? '保存中...' : '報告書名を更新する'}
          </Button>
          {loading && <LoadingSpinner size={20} />}
        </SubmitButtonContainer>
        <Button
          onClick={handleClose}
          colorPattern="monotone"
          variant="outlined"
          disabled={loading}
        >
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  )
}

const ReportNameInput = styled(TextInput)({
  width: '360px',
})

const SubmitButtonContainer = styled('div')({
  position: 'relative',
  display: 'inline-block',
})

const LoadingSpinner = styled(CircularProgress)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: '-10px',
  marginLeft: '-10px',
  color: colorPalette.monotone[0],
})
